import React, { useState } from 'react';
import { DynamicDatePicker } from './DatePicker';

interface FullscreenModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  showDateControls?: boolean;
  startDate?: string;
  endDate?: string;
  onDateChange?: (startDate: string, endDate: string) => void;
  maxDate?: string;
}

export function FullscreenModal({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  showDateControls = false, 
  startDate = '', 
  endDate = '', 
  onDateChange,
  maxDate
}: FullscreenModalProps) {
  const [dates, setDates] = useState({ 
    startDate: new Date(startDate || new Date()), 
    endDate: new Date(endDate || new Date()),
    key: 'selection'
  });

  // Update local state when props change
  React.useEffect(() => {
    setDates({ 
      startDate: new Date(startDate || new Date()), 
      endDate: new Date(endDate || new Date()),
      key: 'selection'
    });
  }, [startDate, endDate]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[100]"
      onClick={onClose}
    >
      <div
        className="w-[95vw] h-[90vh] bg-white rounded-lg flex flex-col relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <h2 className="text-xl font-normal text-gray-800">{title}</h2>
          <div className="flex items-center gap-4">
            {showDateControls && (
              <div className="flex items-center gap-2">
                <DynamicDatePicker
                  defaultDates={dates}
                  maxDate={maxDate || new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().split('T')[0]}
                  minDate={new Date('2024-01-01').toISOString().split('T')[0]}
                  setDate={(dateArray) => {
                    if (dateArray && dateArray.length > 0) {
                      const selectedDates = dateArray[0];
                      setDates(selectedDates);
                      // Convert Date objects back to string format for parent component
                      const startDateStr = selectedDates.startDate.toISOString().split('T')[0];
                      const endDateStr = selectedDates.endDate.toISOString().split('T')[0];
                      onDateChange?.(startDateStr, endDateStr);
                    }
                  }}
                />
              </div>
            )}
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold leading-none p-1"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 p-4 pb-8 overflow-hidden">
          {children}
        </div>
      </div>
    </div>
  );
}
